import React, { useState, useEffect } from 'react';
import Login from './pages/Login/Login';
import AuthCallback from './pages/AuthCallback/AuthCallback';
import CompleteProfile from './pages/CompleteProfile/CompleteProfile';
import Dashboard from './components/Dashboard/Dashboard';
import DashboardNutricionista from './components/DashboardNutricionista/DashboardNutricionista';
import SiteProfissional from './components/SiteProfissional/SiteProfissional';
import GoogleCallback from './components/GoogleCallback';
import { User } from './types/auth';
import authService from './services/authService';
import './styles/global.css';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);

  // Detectar se é callback do Google
  const isGoogleCallback = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const fragment = new URLSearchParams(window.location.hash.substring(1));
    return !!(fragment.get('id_token') ?? urlParams.get('id_token') ?? urlParams.get('error'));
  };

  useEffect(() => {
    // Listener para mudanças de URL
    const handlePopState = () => {
      setCurrentPath(window.location.pathname);
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    // Debug logs removidos para produção
  }, [currentPath, isAuthenticated, user, loading]);

  useEffect(() => {
    // Verificar se usuário já está logado
    const checkAuth = async () => {
      if (authService.isAuthenticated()) {
        try {
          const userData = await authService.getProfile();
          setUser(userData);
          setIsAuthenticated(true);
        } catch (error) {
          authService.logout();
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []); // Array vazio para executar apenas uma vez

  const handleLogin = (userData: User) => {
    setUser(userData);
    setIsAuthenticated(true);
    // Redirecionar para dashboard após login
    window.history.pushState({}, '', '/dashboard');
    setCurrentPath('/dashboard');
  };

  const handleLogout = () => {
    authService.logout();
    setUser(null);
    setIsAuthenticated(false);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{height: '100vh'}}>
        <output className="spinner-border text-success">
          <span className="visually-hidden">Carregando...</span>
        </output>
      </div>
    );
  }

  const renderDashboard = () => {
    if (!user) return null;
    
    switch (user.tipo) {
      case 'nutricionista':
        return <DashboardNutricionista user={{
          role: user.tipo,
          name: user.nome,
          email: user.email,
          avatar: user.avatar
        }} onLogout={handleLogout} />;
      case 'paciente':
      default:
        return <Dashboard user={{
          role: user.tipo,
          name: user.nome,
          email: user.email,
          id: user.id,
          avatar: user.avatar
        }} onLogout={handleLogout} />;
    }
  };

  const renderCurrentView = () => {
    if (isGoogleCallback()) {
      return <GoogleCallback onLogin={handleLogin} />;
    }
    
    if (currentPath === '/auth/callback') {
      return <AuthCallback onLogin={handleLogin} />;
    }
    
    if (currentPath.startsWith('/complete-profile')) {
      return <CompleteProfile onComplete={handleLogin} />;
    }
    
    if (currentPath === '/login') {
      return <Login onLogin={handleLogin} />;
    }
    
    if (currentPath === '/dashboard') {
      if (isAuthenticated) {
        return renderDashboard();
      } else {
        return <Login onLogin={handleLogin} />;
      }
    }
    
    // Rota padrão: site profissional
    return <SiteProfissional />;
  };

  return (
    <div className="App">
      {renderCurrentView()}
    </div>
  );
}

export default App;
